import React, { useEffect, useMemo, useState } from "react";
import { Users } from "lucide-react";
import { schApi } from "@/lib/schApi";
import { field } from "@/components/nds/windows/finance/parts";

const KINDS = [["house", "House"], ["case_manager", "Case manager"], ["phase", "Phase"]];
const chipCls = (on) => `px-2 py-0.5 rounded-full border text-[10px] transition-colors ${on ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/10 text-nex-muted hover:text-white"}`;

/** Load the active residents this user may see (server-filtered) with quick groups; drops ids that are no longer active. */
export function usePickerData(value, onChange) {
  const [data, setData] = useState(null);
  useEffect(() => { schApi.get("/residents-picker").then(setData).catch(() => setData({ residents: [], groups: [] })); }, []);
  useEffect(() => {
    if (!data) return;
    const ok = new Set(data.residents.map((r) => r.id));
    if (value.some((id) => !ok.has(id))) onChange(value.filter((id) => ok.has(id)));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
}

/** Multi-select residents: search, select all shown, clear, and one-tap quick groups (house / case manager / phase). */
export default function ResidentMultiPicker({ value, onChange }) {
  const data = usePickerData(value, onChange);
  const [q, setQ] = useState("");
  const sel = useMemo(() => new Set(value), [value]);
  if (!data) return <div className="text-[10px] text-nex-muted" data-testid="sch-respick-loading">Loading residents…</div>;
  const shown = data.residents.filter((r) => !q.trim() || `${r.name} ${r.house} ${r.case_manager}`.toLowerCase().includes(q.trim().toLowerCase()));
  const toggleGroup = (g) => {
    const all = g.ids.every((id) => sel.has(id));
    onChange(all ? value.filter((id) => !g.ids.includes(id)) : [...new Set([...value, ...g.ids])]);
  };
  return (
    <div data-testid="sch-respick" className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted flex-1">Residents</span>
        <span data-testid="sch-respick-count" className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-nex-gold/40 text-nex-gold rounded-sm text-[10px]"><Users className="h-3 w-3" />{value.length} selected</span>
      </div>
      {KINDS.map(([k, l]) => {
        const gs = data.groups.filter((g) => g.kind === k);
        return gs.length ? (
          <div key={k} className="space-y-1" data-testid={`sch-respick-groups-${k}`}>
            <div className="text-[9px] uppercase tracking-wider text-nex-muted">{l}</div>
            <div className="flex flex-wrap gap-1">{gs.map((g) => {
              const on = g.ids.every((id) => sel.has(id));
              return <button key={g.key} type="button" onClick={() => toggleGroup(g)} aria-pressed={on} data-testid={`sch-respick-group-${g.key.replace(/[^\w-]/g, "-")}`} className={chipCls(on)}>{g.label} · {g.ids.length}</button>;
            })}</div>
          </div>
        ) : null;
      })}
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search residents…" data-testid="sch-respick-search" className={`${field} w-full`} />
      <div className="flex gap-3 text-[10px] font-mono uppercase tracking-[0.14em]">
        <button type="button" onClick={() => onChange([...new Set([...value, ...shown.map((r) => r.id)])])} data-testid="sch-respick-all" className="text-nex-gold">Select all ({shown.length})</button>
        <button type="button" onClick={() => onChange([])} data-testid="sch-respick-clear" className="text-nex-muted hover:text-white">Clear</button>
      </div>
      <ul className="max-h-56 overflow-y-auto border border-white/10 rounded-sm divide-y divide-white/5" data-testid="sch-respick-list">
        {shown.map((r) => (
          <li key={r.id}>
            <label className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-white/[0.03]">
              <input type="checkbox" checked={sel.has(r.id)} onChange={() => onChange(sel.has(r.id) ? value.filter((x) => x !== r.id) : [...value, r.id])} data-testid={`sch-respick-row-${r.id}`} className="accent-[#d4a017]" />
              <span className="min-w-0"><span className="block text-white/90 truncate">{r.name}</span><span className="block text-[10px] text-nex-muted truncate">Phase {r.phase} · {r.house}</span></span>
            </label>
          </li>
        ))}
        {!shown.length && <li className="px-2 py-3 text-[10px] text-nex-muted">No active residents match.</li>}
      </ul>
    </div>
  );
}
